// Thème Maison : glissière légère (défilement natif + aimantation CSS).
// Flèches, points, défilement automatique facultatif (coupé si l'utilisateur préfère moins d'animations),
// lecture de la vidéo de la diapositive visible seulement.

class MxSlider extends HTMLElement {
  connectedCallback() {
    this.track = /** @type {HTMLElement|null} */ (this.querySelector('[data-mx-track]'));
    if (!this.track) return;
    this.prevBtn = this.querySelector('[data-mx-prev]');
    this.nextBtn = this.querySelector('[data-mx-next]');
    this.dots = [...this.querySelectorAll('[data-mx-dot]')];
    this.index = 0;
    this.inView = false;
    this.hovering = false;

    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onEditorSelect = this.onEditorSelect.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('click', this.onClick);
    this.track.addEventListener('keydown', this.onKeydown);
    document.addEventListener('shopify:block:select', this.onEditorSelect);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayMs = reduce ? 0 : (Number(this.dataset.autoplay) || 0) * 1000;
    if (this.autoplayMs) {
      this.addEventListener('pointerenter', () => (this.hovering = true));
      this.addEventListener('pointerleave', () => (this.hovering = false));
      this.addEventListener('focusin', () => (this.hovering = true));
      this.addEventListener('focusout', () => (this.hovering = false));
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        this.inView = entries.some((entry) => entry.isIntersecting);
        this.syncVideos();
        this.syncAutoplay();
      },
      { threshold: 0.35 }
    );
    this.observer.observe(this);
    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:block:select', this.onEditorSelect);
    this.observer?.disconnect();
    clearInterval(this.timer);
  }

  get slides() {
    return /** @type {HTMLElement[]} */ ([...this.track.querySelectorAll('[data-mx-slide]')]);
  }

  goTo(index, behavior = 'smooth') {
    const slides = this.slides;
    if (!slides.length) return;
    const target = (index + slides.length) % slides.length;
    // Chaque diapositive fait toute la largeur de la piste.
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior });
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-mx-prev]')) this.goTo(this.index - 1);
    else if (target.closest('[data-mx-next]')) this.goTo(this.index + 1);
    else {
      const dot = target.closest('[data-mx-dot]');
      if (dot) this.goTo(this.dots.indexOf(dot));
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.index - 1);
    }
  }

  onScroll() {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.update());
  }

  update() {
    const width = this.track.clientWidth || 1;
    const index = Math.round(this.track.scrollLeft / width);
    const slides = this.slides;
    this.index = Math.max(0, Math.min(index, slides.length - 1));

    slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => {
      if (i === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.syncVideos();
  }

  syncVideos() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.index && this.inView) {
        video.muted = true;
        video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  syncAutoplay() {
    clearInterval(this.timer);
    if (!this.autoplayMs || !this.inView || this.slides.length < 2) return;
    this.timer = setInterval(() => {
      if (!this.hovering && !document.hidden) this.goTo(this.index + 1);
    }, this.autoplayMs);
  }

  onEditorSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.track.contains(target)) return;
    const slide = target.closest('[data-mx-slide]');
    const index = this.slides.indexOf(/** @type {HTMLElement} */ (slide));
    if (index >= 0) this.goTo(index, 'auto');
  }
}

if (!customElements.get('mx-slider')) customElements.define('mx-slider', MxSlider);
